<template>
	<div class="p-component p-fluid p-formgrid p-grid">
		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ trainingKey.description }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<InputText type="text" v-model="trainingKey.value.value" disabled />
			<small v-if="!(trainingKey.value.value !== '' && trainingKey.value.value !== null)" class="p-error">{{$t("Squeeze.Training.TrainingKey")}}</small>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Indicator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="indicatorId"
				:options="allIndicators"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				@change="onIndicatorChange"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Anchor') }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="regionAnchorId"
				:options="allRegionAnchors"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				@change="getItemFields"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.DocumentClasses.Table") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="tableId"
				:options="tables"
				:loading="loading"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				disabled
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Column") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="itemField.columnId"
				:options="columnValues"
				:optionLabel="getTranslatedDescription"
				optionValue="id"
				:filter="true"
				:autoFilterFocus="true"
				:loading="loading"
				@show="showDropdownOverlay"
			/>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="itemField.locatorId ? $t('Squeeze.Training.OptionalFallback') : $t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Locators.Value") }}</p>
		</div>
		<div class="p-col-9">
			<div class="p-field p-input-filled p-inputgroup">
				<InputText
					type="text"
					v-model="itemField.valuePattern"
					:placeholder="$t('Squeeze.Locators.Value')"
					@focus="onFocusField('itemFieldValuePattern')"
				/>
				<Button class="active" icon="mdi mdi-auto-fix" @click="onFocusField('itemFieldValuePattern')" />
			</div>
		</div>

		<div class="p-col-3">
			<p v-tooltip.top="$t('Squeeze.Training.Optional')" style="font-size: 1rem; cursor: help;">{{ $t("Squeeze.Training.Locator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="itemField.locatorId"
				:options="allLocators"
				optionLabel="description"
				optionValue="id"
				:showClear="true"
				:placeholder="$t('Squeeze.Training.SelectLocator')"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Region") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<div class="p-inputgroup">
				<span
					v-if="regionBadge"
					class="p-inputtext p-component region-display region-display--set region-display--clickable"
					@click="markCurrentRegion"
				>✓ {{ regionBadge }}</span>
				<span
					v-else
					class="p-inputtext p-component region-display region-display--unset"
				>{{ $t('Squeeze.Training.RegionNotSet') }}</span>
				<Button icon="mdi mdi-vector-rectangle" @click="onFocusField('itemFieldValueRegion')" />
			</div>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.IgnoreSpaces") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="itemField.valueIgnoreWhitespace" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.MultilineReadout") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown v-model="itemField.accumulateFoundValues" :options="booleanOptions" optionLabel="name" optionValue="id" />
		</div>

		<div class="p-col-3">
			<span v-tooltip.top="!canTrain ? $t('Squeeze.Training.PatternOrLocatorRequired') : null">
				<Button
					:label="itemField.id ? $t('Squeeze.Training.Retrain') : $t('Squeeze.Training.Train')"
					:disabled="!canTrain"
					@click="trainItemField"
				/>
			</span>
		</div>
	</div><br/>

	<div class="inner-content">
		<FormPositionTrainingTable
			tableOrigin="itemFields"
			:tableData="allItemFields"
			:loading="loading"
			:tableId="tableId"
			:tables="tables"
			@testData="testItemField"
			@changeData="changeItemField"
			@deleteData="deleteItemField"
			@onMarkRegion="onMarkRegion"
		/>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, onBeforeMount, onMounted, PropType, reactive, ref, watch, computed} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {
	DocumentField,
	DocumentTable,
	DocumentTableColumn,
	FormTrainingItemField, FormTrainingItemRegionAnchor,
	FormTrainingRegionAnchor,
	DocumentLocator
} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import {showDropdownOverlay} from "@/util/StylesHelper";
import FormPositionTrainingTable from "@/apps/squeeze/components/FormPositionTrainingTable.vue";

export default defineComponent({
	name: "FormTrainingRegionAnchor",
	methods: {showDropdownOverlay},
	components: {
		FormPositionTrainingTable,
		InputText,
		Dropdown,
		Button,
	},
	props: {
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		tables: {
			type: Array as PropType<DocumentTable[]>,
			default: () => [],
		},
		itemFieldOfRegionInFormTraining: {
			type: Object as PropType<FormTrainingItemField>,
			default: () => ({}),
		},
		booleanOptions: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
	},
	emits: [
		'testItemField',
		'onFocusField',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current training key field */
		const trainingKey = reactive<DocumentField>({});

		/** Selected Indicator ID */
		const indicatorId = ref<number>(0);

		/** Selected region anchor ID */
		const regionAnchorId = ref<number>(0);

		/** Selected table ID */
		const tableId = ref<number>(0);

		/** Current item field of region in form training */
		const itemField = reactive<FormTrainingItemField>({
			id: 0,
			columnId: 0,
			valuePattern: '',
			valueRegion: {
				page: 0,
				x0: 0,
				y0: 0,
				x1: 0,
				y1: 0,
			},
			valueIgnoreWhitespace: false,
			accumulateFoundValues: false,
			locatorId: undefined,
		});

		/** Show Loading on load data */
		const loading = ref(false);

		/** Columns of the Table */
		const columnValues  = ref<DocumentTableColumn[]>([]);

		/** All item fields */
		const allItemFields = ref<FormTrainingItemField[]>([]);

		/** All region anchors */
		const allRegionAnchors = ref<FormTrainingRegionAnchor[]>([]);

		/** All indicators */
		const allIndicators = ref<FormTrainingItemRegionAnchor[]>([]);

		/** All locators */
		const allLocators = ref<DocumentLocator[]>([]);

		/** Document Class API endpoint */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** Form Training API endpoint */
		const formTrainingApi = ClientManager.getInstance().squeeze.formTraining;

		/** Locator API endpoint */
		const locatorApi = ClientManager.getInstance().squeeze.locator;

		/**
		 * Triggered when a field is focused
		 * @param {string} fieldName
		 */
		const onFocusField = (fieldName: string) => {
			emit("onFocusField", fieldName);
		}

		/** Set indicator ID and tableID */
		const setIndicatorId = () => {
			if (allIndicators.value && allIndicators.value.length && allIndicators.value[0].id) {
				// set the indicator id
				indicatorId.value = allIndicators.value[0].id;
				// set table id
				tableId.value = allIndicators.value[0].tableId!;
			}
		}

		/** Set region anchor ID */
		const setRegionAnchorId = () => {
			if (allRegionAnchors.value[0] && allRegionAnchors.value[0].id) {
				regionAnchorId.value = allRegionAnchors.value[0].id;
			}
		}

		/** Reset item field */
		const resetItemField = () => {
			Object.assign(itemField, {
				id: undefined,
				columnId: 0,
				valuePattern: '',
				valueRegion: { page: 0, x0: 0, y0: 0, x1: 0, y1: 0 },
				valueIgnoreWhitespace: false,
				accumulateFoundValues: false,
				locatorId: undefined,
			});
		}

		/** Get all table column */
		const getAllColumns = () => {
			if (!tableId.value) {
				return;
			}

			loading.value = true;
			documentClassApi.getAllDocumentClassTableColumns(props.documentClassId, tableId.value)
				.then(data => {
					columnValues.value = data;
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all item fields */
		const getItemFields = () => {
			loading.value = true;

			formTrainingApi.getFormItemFields(props.documentClassId, regionAnchorId.value)
				.then(data => {
					allItemFields.value = data;
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					if (err.statusCode === 404) {
						allItemFields.value = [];
					} else {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Train item field */
		const trainItemField = () => {
			loading.value = true;

			let promise;
			if (itemField.id) {
				promise = formTrainingApi.retrainFormItemField(props.documentClassId, itemField.id, itemField, true);
			} else {
				promise = formTrainingApi.trainFormItemField(props.documentClassId, regionAnchorId.value, itemField, false);
			}

			promise
				.then(() => {
					getItemFields();
					resetItemField();
				}).catch((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), err.message);
				}).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Test an item field
		 * @param rowData
		 */
		const testItemField = (rowData: any) => {
			rowData.indicatorId = indicatorId.value;
			rowData.regionAnchorId = regionAnchorId.value;
			rowData.columns = columnValues.value;
			emit("testItemField", rowData);
		}

		/**
		 * Change an item field
		 * @param data
		 */
		const changeItemField = (data: any) => {
			Object.assign(itemField, { locatorId: undefined, ...data });
		}

		/**
		 * Deletes an item field
		 * @param data
		 */
		const deleteItemField = (data: any) => {
			loading.value = true;
			formTrainingApi.deleteFormItemField(props.documentClassId, data.id)
				.then(() => {
					resetItemField();
					getItemFields();
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all region anchors */
		const getAllRegionAnchors = () => {
			loading.value = true;

			formTrainingApi.getFormItemRegionAnchors(props.documentClassId, indicatorId.value)
				.then(data => {
					allRegionAnchors.value = data;

					setRegionAnchorId();
					getItemFields();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					if (err.statusCode === 404) {
						allRegionAnchors.value = [];
					} else {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all Indicators */
		const getAllIndicators = () => {
			loading.value = true;
			formTrainingApi.getFormTableItemIndicator(props.documentClassId, trainingKey.value!.value!)
				.then(data => {
					allIndicators.value = data;

					setIndicatorId();
					getAllRegionAnchors();
					getAllColumns();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					if (err.statusCode === 404) {
						allIndicators.value = [];
					} else {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}
				})).finally(() => {
					loading.value = false;
				});
		}
		/** Get all locators */
		const getAllLocators = () => {
			locatorApi.getAllLocators()
				.then(data => { allLocators.value = data; })
				.catch(response => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ': ' + err.message);
				}));
		}

		/** Update table and region anchors when indicator selection changes */
		const onIndicatorChange = () => {
			const selected = allIndicators.value.find(i => i.id === indicatorId.value);
			if (selected) {
				tableId.value = selected.tableId!;
			}
			regionAnchorId.value = 0;
			itemField.columnId = 0;
			getAllRegionAnchors();
			getAllLocators();
			getAllColumns();
		}

		/** Computed badge string for the current valueRegion */
		const regionBadge = computed(() => {
			const r = itemField.valueRegion;
			if (!r || (r.x0 === 0 && r.y0 === 0 && r.x1 === 0 && r.y1 === 0)) return null;
			const fmt = (n: number | undefined) => Number((n ?? 0).toFixed(1));
			return `p.${r.page ?? 0} · (x0:${fmt(r.x0)}, y0:${fmt(r.y0)}) → (x1:${fmt(r.x1)}, y1:${fmt(r.y1)})`;
		});

		/** Whether the form is valid enough to trigger training */
		const canTrain = computed(() =>
			!!regionAnchorId.value
			&& !!itemField.columnId
			&& (itemField.valuePattern !== '' || !!itemField.locatorId)
			&& !!regionBadge.value
			&& trainingKey.value?.value !== ''
			&& trainingKey.value?.value != null
		);
		/** Mark the current form region in the viewer */
		const markCurrentRegion = () => {
			if (itemField.valueRegion) {
				emit("onMarkRegion", [itemField.valueRegion]);
			}
		}

		/**
		 * Set marker regions by clicking a row
		 * @param event
		 */
		const onMarkRegion = (event: {originalEvent: PointerEvent; data: any; index: number}) => {
			const row = event.data;
			emit("onMarkRegion", [row.valueRegion]);
		}

		onMounted(() => {
			Object.assign(itemField, props.itemFieldOfRegionInFormTraining);
			resetItemField();
			getAllIndicators();
			getAllLocators();
		})

		onBeforeMount(() => {
			Object.assign(trainingKey, props.trainingKeyField);
		})

		watch(
			() => [props.itemFieldOfRegionInFormTraining.valuePattern, props.itemFieldOfRegionInFormTraining.valueRegion],
			() => {
				itemField.valuePattern = props.itemFieldOfRegionInFormTraining.valuePattern;
				itemField.valueRegion = props.itemFieldOfRegionInFormTraining.valueRegion;
			},
			{ deep: true }
		)

		return {
			getTranslatedDescription,
			t,
			toast,
			trainingKey,
			indicatorId,
			regionAnchorId,
			tableId,
			itemField,
			loading,
			columnValues,
			allItemFields,
			allRegionAnchors,
			allIndicators,
			allLocators,
			regionBadge,
			canTrain,
			onFocusField,
			markCurrentRegion,
			getItemFields,
			trainItemField,
			testItemField,
			changeItemField,
			deleteItemField,
			getAllColumns,
			getAllRegionAnchors,
			onIndicatorChange,
			getAllLocators,
			onMarkRegion,
		}
	},
});
</script>

<style scoped>

.inner-content {
	height: calc(100% - 11.8rem);
}

.region-display {
	background: var(--surface-ground);
	font-family: monospace;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
}

.region-display--set {
	color: var(--green-400);
}

.region-display--clickable {
	cursor: pointer;
}

.region-display--clickable:hover {
	background: var(--surface-hover);
}

.region-display--unset {
	color: var(--red-400);
	font-style: italic;
}

</style>
